import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AddInvoiceTDS = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [invoices, setInvoices] = useState([]);
    const [formData, setFormData] = useState({
        invoice_id: "",
        tds_percentage: "",
        tds_amount: "",
        note: ""
    });

    const [inputMode, setInputMode] = useState("percentage");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        dispatch(loadingTrue());
        try {
            // 🔥 Machan, unga structure-la summary values nested-ah irukkum
            const res = await Handler({ method: "GET", url: "/invoice/list.php" });
            if (res?.success && res.data) {
                const options = res.data.map(item => ({
                    // 🔥 Inga thaan change: inv object kulla invoice and summary separate-ah irukkum
                    value: item.invoice?.id, 
                    label: `${item.invoice?.invoice_no} - ${item.client?.name} (Amt: ₹${Math.round(item.summary?.total_amount || 0)})`
                }));
                setInvoices(options);
            }
        } catch (err) {
            toast.error("Error loading invoices");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.invoice_id) return toast.error("Please select an invoice");
        if (!formData.tds_amount) return toast.error("Please enter TDS amount / Percentage");

        dispatch(loadingTrue());
        const payload = {
            invoice_id: formData.invoice_id,
            tds_percentage: inputMode === "percentage" ? formData.tds_percentage : null,
            tds_amount: inputMode === "amount" ? formData.tds_amount : null,
            note: formData.note
        };

        try {
            const res = await Handler({ method: "POST", url: "/tds/create.php", data: payload });
            if (res?.success) {
                Swal.fire("Applied!", "TDS recorded successfully", "success");
                navigate("/tds"); // Navigating to TDS list
            } else {
                toast.error(res?.message || "Failed to apply TDS");
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    // Styling for React Select
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            borderRadius: '8px',
        }),
    };

    return (
        <div className="page-container p-1 bg-light min-vh-100">
            <div className="page-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Apply Invoice TDS</h3>
                        <p className="text-muted small m-0">Select an invoice and provide deduction details.</p>
                    </div>
                    <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4">
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="small fw-bold opacity-75">TARGET INVOICE</Label>
                                    <Select
                                        options={invoices}
                                        styles={customSelectStyles}
                                        placeholder="Search by Invoice No or Client Name..."
                                        onChange={(opt) => setFormData({ ...formData, invoice_id: opt?.value })}
                                        isClearable
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="bg-light p-3 rounded-3 mb-4 mt-2">
                            <Label className="small fw-bold opacity-75 d-block mb-3 text-uppercase">Deduction Method</Label>
                            <div className="d-flex gap-4">
                                <FormGroup check>
                                    <Label check className="pointer">
                                        <Input type="radio" name="mode" checked={inputMode === "percentage"} onChange={() => setInputMode("percentage")} /> 
                                        Percentage Basis (%)
                                    </Label>
                                </FormGroup>
                                <FormGroup check>
                                    <Label check className="pointer">
                                        <Input type="radio" name="mode" checked={inputMode === "amount"} onChange={() => setInputMode("amount")} /> 
                                        Fixed Amount (₹)
                                    </Label>
                                </FormGroup>
                            </div>
                        </div>

                        <Row>
                            {inputMode === "percentage" ? (
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small fw-bold opacity-75">TDS PERCENTAGE</Label>
                                        <InputGroup>
                                            <Input
                                                type="number"
                                                name="tds_percentage"
                                                placeholder="e.g. 0.10"
                                                step="any"
                                                value={formData.tds_percentage}
                                                onChange={handleChange}
                                                className="fw-bold"
                                            />
                                            <InputGroupText className="bg-white">%</InputGroupText>
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                            ) : (
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small fw-bold opacity-75">TDS AMOUNT</Label>
                                        <InputGroup>
                                            <InputGroupText className="bg-white text-danger fw-bold">₹</InputGroupText>
                                            <Input
                                                type="number"
                                                name="tds_amount"
                                                placeholder="0.00"
                                                step="any"
                                                value={formData.tds_amount}
                                                onChange={handleChange}
                                                className="fw-bold"
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                </Col>
                            )}

                            <Col md={12}>
                                <FormGroup className="mt-2">
                                    <Label className="small fw-bold opacity-75">REMARKS / NOTE</Label>
                                    <Input
                                        type="textarea"
                                        name="note"
                                        placeholder="Reason for TDS deduction..."
                                        rows="3"
                                        value={formData.note}
                                        onChange={handleChange}
                                        style={{ borderRadius: '10px' }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                            <Button type="button" color="light" className="px-4" onClick={() => setFormData({ invoice_id: "", tds_percentage: "", tds_amount: "", note: "" })}>
                                Reset
                            </Button>
                            <Button type="submit" className="add-btn px-5 shadow">
                                <i className="bx bx-check-double me-1"></i> Confirm & Apply
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default AddInvoiceTDS;